import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { allServices } from '../data/services';
import { budgetRanges, jamaicaParishes } from '../data/stats';
import { company } from '../data/company';
import { submitContactForm } from '../lib/supabase';
import AnimatedSection from '../components/ui/AnimatedSection';
import Button from '../components/ui/Button';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from 'lucide-react';

const clientTypes = ['Low Income', 'Middle Income', 'High Income', 'International Investors'];

interface FormData {
  name: string;
  email: string;
  phone: string;
  project_type: string;
  location: string;
  client_type: string;
  budget_range: string;
  message: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  project_type: '',
  location: '',
  client_type: '',
  budget_range: '',
  message: '',
};

export default function Contact() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = (currentStep: number): boolean => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          setError('Please enter your name');
          return false;
        }
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        return true;
      case 2:
        if (!formData.project_type) {
          setError('Please select a service type');
          return false;
        }
        if (!formData.location) {
          setError('Please select a location');
          return false;
        }
        return true;
      case 3:
        if (!formData.client_type) {
          setError('Please select your client type');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    setError('');

    try {
      await submitContactForm(formData);
      setIsSuccess(true);
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses =
    'w-full px-4 py-3 border border-charcoal-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-charcoal-900 placeholder:text-charcoal-400';
  const labelClasses = 'block text-sm font-medium text-charcoal-700 mb-2';
  const selectClasses =
    'w-full px-4 py-3 border border-charcoal-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-charcoal-900 bg-white appearance-none cursor-pointer';

  if (isSuccess) {
    return (
      <>
        <section className="pt-32 pb-16 bg-gradient-to-b from-charcoal-900 to-charcoal-800">
          <div className="container-custom">
            <AnimatedSection>
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="heading-1 text-white mb-6">Contact Us</h1>
                <p className="text-xl md:text-2xl font-semibold text-charcoal-200">
                  Get In Touch
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-lg mx-auto text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-primary-600" />
              </div>
              <h2 className="heading-2 text-charcoal-900 mb-4">
                Message Sent Successfully
              </h2>
              <p className="text-charcoal-600 mb-8">
                Thank you for reaching out. Our team will review your inquiry
                and get back to you within 1-2 business days.
              </p>
              <Button href="/">Return to Homepage</Button>
            </motion.div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-b from-charcoal-900 to-charcoal-800">
        <div className="container-custom">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="heading-1 text-white mb-6">
                Contact Us
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-charcoal-200 mb-4">
                Let's Discuss Your Project
              </p>
              <p className="text-base md:text-lg text-charcoal-300 leading-relaxed">
                Ready to start your construction project? Fill out the form
                below and our team will get back to you with a personalized
                consultation.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section-padding bg-charcoal-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <div className="bg-white rounded-2xl border border-charcoal-100 p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-8">
                    {[1, 2, 3].map((s) => (
                      <div key={s} className="flex items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
                            step >= s
                              ? 'bg-primary-600 text-white'
                              : 'bg-charcoal-100 text-charcoal-400'
                          }`}
                        >
                          {s}
                        </div>
                        {s < 3 && (
                          <div
                            className={`w-16 md:w-24 h-1 mx-2 rounded transition-colors ${
                              step > s ? 'bg-primary-600' : 'bg-charcoal-100'
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <form onSubmit={handleSubmit}>
                    <AnimatePresence mode="wait">
                      {step === 1 && (
                        <motion.div
                          key="step1"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="space-y-6"
                        >
                          <h3 className="text-xl font-semibold text-charcoal-900 mb-2">
                            Your Information
                          </h3>
                          <p className="text-charcoal-600 text-sm mb-6">
                            Tell us how to reach you.
                          </p>

                          <div>
                            <label className={labelClasses}>
                              Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => updateField('name', e.target.value)}
                              className={inputClasses}
                              placeholder="John Smith"
                            />
                          </div>

                          <div>
                            <label className={labelClasses}>
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) => updateField('email', e.target.value)}
                              className={inputClasses}
                              placeholder="john@example.com"
                            />
                          </div>

                          <div>
                            <label className={labelClasses}>Phone Number</label>
                            <input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => updateField('phone', e.target.value)}
                              className={inputClasses}
                              placeholder="+1 (876) 000-0000"
                            />
                          </div>
                        </motion.div>
                      )}

                      {step === 2 && (
                        <motion.div
                          key="step2"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="space-y-6"
                        >
                          <h3 className="text-xl font-semibold text-charcoal-900 mb-2">
                            Project Details
                          </h3>
                          <p className="text-charcoal-600 text-sm mb-6">
                            Tell us about your construction needs.
                          </p>

                          <div>
                            <label className={labelClasses}>
                              Service Type <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <select
                                value={formData.project_type}
                                onChange={(e) => updateField('project_type', e.target.value)}
                                className={selectClasses}
                              >
                                <option value="">Select a service</option>
                                {allServices.map((service) => (
                                  <option key={service.id} value={service.title}>
                                    {service.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className={labelClasses}>
                              Project Location <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <select
                                value={formData.location}
                                onChange={(e) => updateField('location', e.target.value)}
                                className={selectClasses}
                              >
                                <option value="">Select a parish</option>
                                {jamaicaParishes.map((parish) => (
                                  <option key={parish} value={parish}>
                                    {parish}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {step === 3 && (
                        <motion.div
                          key="step3"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="space-y-6"
                        >
                          <h3 className="text-xl font-semibold text-charcoal-900 mb-2">
                            Additional Information
                          </h3>
                          <p className="text-charcoal-600 text-sm mb-6">
                            Help us understand your project better.
                          </p>

                          <div>
                            <label className={labelClasses}>
                              Client Type <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-1 gap-3">
                              {clientTypes.map((type) => (
                                <label
                                  key={type}
                                  className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                                    formData.client_type === type
                                      ? 'border-primary-500 bg-primary-50'
                                      : 'border-charcoal-200 hover:border-charcoal-300'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="client_type"
                                    value={type}
                                    checked={formData.client_type === type}
                                    onChange={(e) => updateField('client_type', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                      formData.client_type === type
                                        ? 'border-primary-600'
                                        : 'border-charcoal-300'
                                    }`}
                                  >
                                    {formData.client_type === type && (
                                      <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />
                                    )}
                                  </div>
                                  <span className="text-charcoal-900">{type}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className={labelClasses}>Budget Range</label>
                            <div className="relative">
                              <select
                                value={formData.budget_range}
                                onChange={(e) => updateField('budget_range', e.target.value)}
                                className={selectClasses}
                              >
                                <option value="">Select budget range</option>
                                {budgetRanges.map((range) => (
                                  <option key={range} value={range}>
                                    {range}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className={labelClasses}>Project Description</label>
                            <textarea
                              value={formData.message}
                              onChange={(e) => updateField('message', e.target.value)}
                              rows={4}
                              className={inputClasses}
                              placeholder="Tell us about your project requirements, timeline, and any specific needs..."
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {error && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-red-600 text-sm mt-4"
                      >
                        {error}
                      </motion.p>
                    )}

                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-charcoal-100">
                      {step > 1 ? (
                        <button
                          type="button"
                          onClick={prevStep}
                          className="flex items-center gap-2 text-charcoal-600 hover:text-charcoal-900 font-medium transition-colors"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </button>
                      ) : (
                        <div />
                      )}

                      {step < 3 ? (
                        <Button type="button" onClick={nextStep}>
                          Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      ) : (
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              Submit Request
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              </AnimatedSection>
            </div>

            <div>
              <AnimatedSection delay={0.2}>
                <div className="bg-white rounded-2xl border border-charcoal-100 p-8 sticky top-28">
                  <h3 className="font-semibold text-lg text-charcoal-900 mb-6">
                    Contact Information
                  </h3>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-50 rounded-lg">
                        <MapPin className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal-900">Location</p>
                        <p className="text-sm text-charcoal-600">
                          {company.coverage}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-50 rounded-lg">
                        <Mail className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal-900">Email</p>
                        <a
                          href="mailto:info@lifetimecreation.com"
                          className="text-sm text-primary-600 hover:underline"
                        >
                          info@lifetimecreation.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-50 rounded-lg">
                        <Phone className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal-900">Phone</p>
                        <p className="text-sm text-charcoal-600">Contact Us</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-50 rounded-lg">
                        <Clock className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal-900">
                          Response Time
                        </p>
                        <p className="text-sm text-charcoal-600">
                          1-2 Business Days
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-charcoal-100">
                    <p className="text-sm text-charcoal-600">
                      <span className="font-medium text-charcoal-900">
                        Safety Certified:
                      </span>{' '}
                      {company.safetyStandard}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
